import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  createFeed as createFeedService,
  updateFeedById as updateFeedByIdService,
  deleteFeedById as deleteFeedByIdService,
  archiveFeedById as archiveFeedByIdService,
  getFeedsSkipLimit as getFeedsService,
  previewFeedNotification as previewFeedNotificationService,
  sendFeedNotification as sendFeedNotificationService
} from '../../services/feed/feed.services';

import { validate } from '../../utils';
import { handlerErrorV2 } from '../../utils/errors';
import { updateFeedsList, updateFeedsListMethod } from '../../store/actions/feed.actions';
import isEmpty from 'lodash/isEmpty';
import useUpload from '../upload/useUpload';

const useFeeds = () => {
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const { uploadFile } = useUpload();
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [errors, setErrors] = useState({});

  const getFeeds = useCallback(async (filter, skip, rowsPerPage) => {
    setIsLoadingList(true);
    try {
      const query = {
        rangeExpireAt: filter?.rangeExpireAt,
        rangePublishedAt: filter?.rangePublishedAt,
        status: filter?.status,
        roles: filter?.rolesId,
        yearLevels: filter?.yearLevelId,
        title: filter?.title,
        postType: filter?.postType?.value
      };
      const response = await getFeedsService(skip, rowsPerPage, query);
      dispatch(updateFeedsList(response?.data || []));
    } catch (error) {
      setErrors({ message: error.message || 'An error ocurred' });
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  const validateForm = (form) => {
    const fieldRules = {
      title: 'required',
      content: 'required',
      viewRoles: 'required'
    };

    const errors = validate(fieldRules, form);
    if (!isEmpty(errors)) {
      return { error: true, ...errors };
    }

    return { error: false };
  };

  const createFeed = async (form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result;
    }

    try {
      const response = await createFeedService(form);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateFeedsListMethod('POST', response.data));
        enqueueSnackbar('Feed created successfully', { variant: 'success' });
        return { error: false, data: response.data };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const updateFeed = async (feedId, form = {}) => {
    const result = validateForm(form);
    if (result.error) {
      return result;
    }

    try {
      const response = await updateFeedByIdService(feedId, form);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateFeedsListMethod('PUT', { _id: feedId, ...response.data }));
        enqueueSnackbar('Feed updated successfully', { variant: 'success' });
        return { error: false, data: response.data };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const archiveFeed = async (feedId) => {
    try {
      const response = await archiveFeedByIdService(feedId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateFeedsListMethod('DELETE', feedId));
        enqueueSnackbar('Feed archived successfully', { variant: 'success' });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const uploadAttachment = async (formData) => {
    try {
      const attachment = formData.attachment
        ?.filter((item) => item.type === 'file' && item.file)
        ?.map((item) => ({
          ...item,
          id: item._id || item.id,
          type: item.mimetype
        }));

      if (attachment?.length > 0) {
        const resultUpload = await uploadFile(attachment, 'feed');
        if (resultUpload.error) {
          return { error: true, message: resultUpload.message };
        }
        let errorInAttachments = false;
        let errorData = {};
        resultUpload.data.forEach((uploadedFile) => {
          if (uploadedFile.error) {
            errorInAttachments = true;
            errorData[uploadedFile.name] = uploadedFile.error;
          }
        });
        if (errorInAttachments) {
          return { error: true, message: 'One or more attachments failed to upload.', details: errorData };
        }
        const newMedia = resultUpload.data;
        formData.attachment = formData.attachment
          .map((item) => {
            if (item.type === 'file') {
              const find = newMedia.find((attachment) => attachment.id === item.id);
              if (find) {
                return { ...item, ...find };
              }
            }
            return item;
          })
          .filter((item) => item.link);
      }

      return { error: false, data: formData.attachment };
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const deleteFeed = async (feedId) => {
    try {
      const response = await deleteFeedByIdService(feedId);
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        };
      } else {
        dispatch(updateFeedsListMethod('DELETE', feedId));
        enqueueSnackbar('Feed deleted successfully', { variant: 'success' });
        return { error: false };
      }
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const previewNotification = async (feedId, roles = [], yearLevels = []) => {
    try {
      const response = await previewFeedNotificationService(feedId, { roles, yearLevels });
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error occurred' };
      }
      return { error: false, data: response.data };
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  const sendNotification = async (feedId, roles = [], yearLevels = []) => {
    try {
      const response = await sendFeedNotificationService(feedId, { roles, yearLevels });
      if (!response.data && response.error) {
        return { error: true, message: response.error.message || 'An error occurred' };
      }
      enqueueSnackbar(`Emails sent successfully (${response.data?.emailsSent || 0})`, { variant: 'success' });
      return { error: false, data: response.data };
    } catch (error) {
      const message = handlerErrorV2(error);
      return { error: true, message };
    }
  };

  return {
    errors,
    isLoadingList,
    getFeeds,
    createFeed,
    updateFeed,
    deleteFeed,
    archiveFeed,
    uploadAttachment,
    previewNotification,
    sendNotification
  };
};

export default useFeeds;
